import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Apps } from '../services/apps';
import { queryKeys } from './queryKeys';

// Statuses where the app is still settling (just deployed / coming up / going
// down). While ANY row is in one of these, the list polls so a freshly-deployed
// app shows up as 'provisioning' and its pill advances live — instead of the
// list looking empty until the next manual refresh.
const TRANSITIONAL_STATUSES = new Set([
  'provisioning', 'pending', 'deploying', 'building', 'starting',
  'stopping', 'restarting', 'upgrading',
]);

/**
 * List deployed app instances for a project, React-Query cached.
 *
 * Replaces the manual useEffect/useState fetch loop in AppsPage. The list is
 * two merged sources (preserved EXACTLY from the old page logic):
 *   1. Project-scoped CLOUD apps (Fargate) — the only rows the `?projectId=`
 *      GSI query returns.
 *   2. Account-scoped SOLO apps (dedicated EC2) — these have no projectId so
 *      they're absent from the GSI; we pull the account-wide list and keep
 *      ONLY deployMode==='solo' rows (don't surface other projects' cloud
 *      apps). Terminated solo rows are KEPT (their CloudWatch logs persist
 *      14d and the row is the only entry point to that history).
 *
 * `live` controls the ECS reconcile: the old page did a fast first paint with
 * the STORED status (`live:false`) then a background `live:true` refresh.
 * React Query gives us that for free via cache + a single `live:true` read,
 * so we ask for the live status directly — same end state, one fewer request.
 */
export const useApps = (projectId, options = {}) => {
  return useQuery({
    queryKey: queryKeys.apps.list(projectId),
    queryFn: async () => {
      const [projResp, acctResp] = await Promise.all([
        Apps.listInstances({ projectId, live: true }),
        Apps.listInstances({ live: true }).catch((e) => {
          // Solo is additive — a failure here must not blank the cloud list.
          console.error('Failed to fetch solo apps:', e);
          return { apps: [] };
        }),
      ]);
      const cloud = Array.isArray(projResp?.apps) ? projResp.apps : [];
      const solo = (Array.isArray(acctResp?.apps) ? acctResp.apps : [])
        .filter((a) => a.deployMode === 'solo');
      return [...cloud, ...solo];
    },
    enabled: !!projectId,
    // Short stale window so navigating to the list (e.g. right after a deploy)
    // refetches and shows the new app instead of serving a 5-min-old cache.
    staleTime: 10 * 1000,
    // Auto-poll while any app is still settling, so a just-deployed app appears
    // as 'provisioning' and its status pill advances without a manual refresh.
    refetchInterval: (query) => {
      const data = query?.state?.data;
      return Array.isArray(data)
        && data.some((a) => TRANSITIONAL_STATUSES.has(String(a?.status || '').toLowerCase()))
        ? 8000
        : false;
    },
    ...options,
  });
};

/**
 * One deployed CLOUD app instance (GET /apps/:instanceId), React-Query cached.
 * Used by AppDetailPage. Detail caching means navigating list→detail→list→detail
 * within the staleTime window reads from cache instead of refetching.
 */
export const useApp = (instanceId, options = {}) => {
  return useQuery({
    queryKey: queryKeys.apps.detail(instanceId),
    queryFn: () => Apps.getInstance(instanceId),
    enabled: !!instanceId,
    // Short stale window so the detail page reflects a fresh deploy/status
    // change instead of a 5-min-old snapshot (mirrors the list).
    staleTime: 10 * 1000,
    // Poll while the app is still settling so its status pill advances live on
    // the detail page (same transitional set the list uses).
    refetchInterval: (query) => {
      const status = String(query?.state?.data?.status || '').toLowerCase();
      return TRANSITIONAL_STATUSES.has(status) ? 8000 : false;
    },
    ...options,
  });
};

/**
 * Destroy a CLOUD app, then keep the cache honest:
 *   - invalidate the project's app LIST  → the destroyed row leaves the table
 *   - remove the app's DETAIL entry      → no ghost detail served from cache
 *
 * `projectId` is required so we invalidate the right list. Pass the
 * `instanceId` to `mutate()`.
 */
export const useDestroyApp = (projectId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (instanceId) => Apps.destroy(instanceId),
    // OPTIMISTIC removal: destroyApp on the backend is synchronous + slow — it
    // tears the Fargate resources DOWN before stamping destroyedAt, so the row
    // would otherwise linger (as 'stopping') in the list/sidebar for the whole
    // teardown (and `Apps.destroy()` doesn't even resolve until then). Drop the
    // row from the caches the instant the user clicks; roll back on failure.
    onMutate: async (instanceId) => {
      const listKey = queryKeys.apps.list(projectId);
      await queryClient.cancelQueries({ queryKey: listKey });
      await queryClient.cancelQueries({ queryKey: ['workspace', 'sidebar'] });
      const prevList = queryClient.getQueryData(listKey);
      queryClient.setQueryData(listKey, (old) =>
        Array.isArray(old) ? old.filter((a) => a.instanceId !== instanceId) : old);
      // Same for every per-project sidebar cache.
      queryClient.setQueriesData({ queryKey: ['workspace', 'sidebar'] }, (old) =>
        old && Array.isArray(old.apps)
          ? { ...old, apps: old.apps.filter((a) => a.instanceId !== instanceId) }
          : old);
      return { prevList, listKey };
    },
    onError: (_err, _instanceId, ctx) => {
      // Roll back — the app didn't actually get destroyed.
      if (ctx?.prevList !== undefined) queryClient.setQueryData(ctx.listKey, ctx.prevList);
      queryClient.invalidateQueries({ queryKey: ['workspace', 'sidebar'] });
    },
    onSettled: (_data, _err, instanceId) => {
      // Reconcile once the call returns (destroyedAt now stamped server-side).
      queryClient.invalidateQueries({ queryKey: queryKeys.apps.list(projectId) });
      queryClient.removeQueries({ queryKey: queryKeys.apps.detail(instanceId) });
      queryClient.invalidateQueries({ queryKey: ['workspace', 'sidebar'] });
    },
  });
};

/**
 * Destroy a SOLO app (DELETE /apps/solo/:slug). Solo rows are KEPT in the list
 * (terminated-in-place) so their logs stay reachable — so we invalidate the
 * list (a refetch re-derives the terminated row) rather than dropping it. No
 * detail entry to purge (solo detail is keyed by slug on a separate path and
 * is not cached here yet). Pass the `slug` to `mutate()`.
 */
export const useDestroySoloApp = (projectId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (slug) => Apps.destroySolo(slug),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.apps.list(projectId) });
      // Account-wide sidebar excludes destroyed apps — refresh it too.
      queryClient.invalidateQueries({ queryKey: ['workspace', 'sidebar'] });
    },
  });
};
